import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AlgorithmSettings = () => {
  const [algorithmParams, setAlgorithmParams] = useState({
    // Core PTS-WIZARD-V3 Parameters
    period1: 10,
    period2: 21,
    source: 'hlc3',
    smoothing: 3,
    upper_threshold: 53,
    lower_threshold: -53,
    extreme_upper: -75,
    extreme_lower: 45,
    momentum_period: -65,
    rsi_period: 14,
    stoch_k: 30,
    stoch_d: 70,
    bb_period: 60,
    bb_multiplier: 150,
    atr_multiplier: 2.5,
    volume_factor: 1.5,
    price_level_high: 'high',
    price_level_low: 'low',
    fibonacci_1: 4,
    fibonacci_2: 8,
    fibonacci_3: 16,
    retracement_1: 0.5,
    retracement_2: 0.618,
    retracement_3: 0.764,
    retracement_4: 0.854,
    ma_short: 10,
    ma_long: 6,
    signal_strength: 75,
    noise_filter: 2,
    trend_period: 20,
    volatility_period: 100,
    risk_factor: 0.15,
    position_size: 10,
    stop_loss: 0.5,
    take_profit: 1.5,
    trailing_stop: 0.5,
    max_positions: 20,
    correlation_period: 20,
    golden_ratio_1: 0.618,
    golden_ratio_2: 0.786
  });

  const [selectedPreset, setSelectedPreset] = useState('custom');
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const presetOptions = [
    { value: 'custom', label: 'Custom Configuration' },
    { value: 'conservative', label: 'Conservative Trading' },
    { value: 'aggressive', label: 'Aggressive Trading' },
    { value: 'scalping', label: 'Scalping Strategy' },
    { value: 'swing', label: 'Swing Trading' },
    { value: 'longterm', label: 'Long-term Investment' }
  ];

  const sourceOptions = [
    { value: 'hlc3', label: 'HLC3 (High-Low-Close/3)' },
    { value: 'ohlc4', label: 'OHLC4 (Open-High-Low-Close/4)' },
    { value: 'close', label: 'Close Price' },
    { value: 'high', label: 'High Price' },
    { value: 'low', label: 'Low Price' }
  ];

  const handleParamChange = (param, value) => {
    setAlgorithmParams(prev => ({
      ...prev,
      [param]: value
    }));
    setSelectedPreset('custom');
  };

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      // Load preset configurations
      const presetConfigs = {
        conservative: {
          ...algorithmParams,
          risk_factor: 0.05,
          stop_loss: 0.3,
          take_profit: 1.0,
          signal_strength: 85
        },
        aggressive: {
          ...algorithmParams,
          risk_factor: 0.25,
          stop_loss: 0.8,
          take_profit: 2.0,
          signal_strength: 60
        },
        scalping: {
          ...algorithmParams,
          period1: 5,
          period2: 13,
          risk_factor: 0.1,
          stop_loss: 0.2,
          take_profit: 0.5
        },
        swing: {
          ...algorithmParams,
          period1: 20,
          period2: 50,
          risk_factor: 0.15,
          stop_loss: 1.0,
          take_profit: 3.0
        },
        longterm: {
          ...algorithmParams,
          period1: 50,
          period2: 200,
          risk_factor: 0.08,
          stop_loss: 2.0,
          take_profit: 5.0
        }
      };
      setAlgorithmParams(presetConfigs?.[preset]);
    }
  };

  const handleExportConfig = () => {
    const configData = JSON.stringify(algorithmParams, null, 2);
    const blob = new Blob([configData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pts-wizard-v3-config-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportConfig = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e?.target?.result);
          setAlgorithmParams(config);
          setSelectedPreset('custom');
        } catch (error) {
          console.error('Invalid configuration file');
        }
      };
      reader?.readAsText(file);
    }
  };

  const handleResetToDefaults = () => {
    setAlgorithmParams({
      period1: 10,
      period2: 21,
      source: 'hlc3',
      smoothing: 3,
      upper_threshold: 53,
      lower_threshold: -53,
      extreme_upper: -75,
      extreme_lower: 45,
      momentum_period: -65,
      rsi_period: 14,
      stoch_k: 30,
      stoch_d: 70,
      bb_period: 60,
      bb_multiplier: 150,
      atr_multiplier: 2.5,
      volume_factor: 1.5,
      price_level_high: 'high',
      price_level_low: 'low',
      fibonacci_1: 4,
      fibonacci_2: 8,
      fibonacci_3: 16,
      retracement_1: 0.5,
      retracement_2: 0.618,
      retracement_3: 0.764,
      retracement_4: 0.854,
      ma_short: 10,
      ma_long: 6,
      signal_strength: 75,
      noise_filter: 2,
      trend_period: 20,
      volatility_period: 100,
      risk_factor: 0.15,
      position_size: 10,
      stop_loss: 0.5,
      take_profit: 1.5,
      trailing_stop: 0.5,
      max_positions: 20,
      correlation_period: 20,
      golden_ratio_1: 0.618,
      golden_ratio_2: 0.786
    });
    setSelectedPreset('custom');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">PTS-WIZARD-V3 Algorithm Settings</h3>
          <p className="text-sm text-muted-foreground">Configure advanced trading algorithm parameters</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            iconName={isPreviewMode ? "EyeOff" : "Eye"}
            iconPosition="left"
          >
            {isPreviewMode ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>
      </div>
      {/* Preset Selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-medium text-foreground">Configuration Presets</h4>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportConfig}
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
            <label>
              <input
                type="file"
                accept=".json"
                onChange={handleImportConfig}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                asChild
              >
                Import
              </Button>
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetToDefaults}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Reset
            </Button>
          </div>
        </div>
        
        <Select
          label="Trading Strategy Preset"
          description="Choose a pre-configured strategy or customize your own"
          options={presetOptions}
          value={selectedPreset}
          onChange={handlePresetChange}
        />
      </div>
      {/* Core Parameters */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Core Algorithm Parameters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Period 1"
            type="number"
            value={algorithmParams?.period1}
            onChange={(e) => handleParamChange('period1', parseInt(e?.target?.value))}
            description="Primary calculation period"
          />
          <Input
            label="Period 2"
            type="number"
            value={algorithmParams?.period2}
            onChange={(e) => handleParamChange('period2', parseInt(e?.target?.value))}
            description="Secondary calculation period"
          />
          <Select
            label="Price Source"
            options={sourceOptions}
            value={algorithmParams?.source}
            onChange={(value) => handleParamChange('source', value)}
            description="Price data source for calculations"
          />
          <Input
            label="Smoothing Factor"
            type="number"
            value={algorithmParams?.smoothing}
            onChange={(e) => handleParamChange('smoothing', parseInt(e?.target?.value))}
            description="Signal smoothing parameter"
          />
          <Input
            label="Upper Threshold"
            type="number"
            value={algorithmParams?.upper_threshold}
            onChange={(e) => handleParamChange('upper_threshold', parseInt(e?.target?.value))}
            description="Upper signal threshold"
          />
          <Input
            label="Lower Threshold"
            type="number"
            value={algorithmParams?.lower_threshold}
            onChange={(e) => handleParamChange('lower_threshold', parseInt(e?.target?.value))}
            description="Lower signal threshold"
          />
        </div>
      </div>
      {/* Technical Indicators */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Technical Indicators</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="RSI Period"
            type="number"
            value={algorithmParams?.rsi_period}
            onChange={(e) => handleParamChange('rsi_period', parseInt(e?.target?.value))}
            description="Relative Strength Index period"
          />
          <Input
            label="Stochastic %K"
            type="number"
            value={algorithmParams?.stoch_k}
            onChange={(e) => handleParamChange('stoch_k', parseInt(e?.target?.value))}
            description="Stochastic %K period"
          />
          <Input
            label="Stochastic %D"
            type="number"
            value={algorithmParams?.stoch_d}
            onChange={(e) => handleParamChange('stoch_d', parseInt(e?.target?.value))}
            description="Stochastic %D period"
          />
          <Input
            label="Bollinger Bands Period"
            type="number"
            value={algorithmParams?.bb_period}
            onChange={(e) => handleParamChange('bb_period', parseInt(e?.target?.value))}
            description="Bollinger Bands calculation period"
          />
          <Input
            label="BB Multiplier"
            type="number"
            value={algorithmParams?.bb_multiplier}
            onChange={(e) => handleParamChange('bb_multiplier', parseFloat(e?.target?.value))}
            description="Bollinger Bands standard deviation multiplier"
          />
          <Input
            label="ATR Multiplier"
            type="number"
            step="0.1"
            value={algorithmParams?.atr_multiplier}
            onChange={(e) => handleParamChange('atr_multiplier', parseFloat(e?.target?.value))}
            description="Average True Range multiplier"
          />
        </div>
      </div>
      {/* Fibonacci & Golden Ratios */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Fibonacci & Golden Ratios</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            label="Fibonacci Level 1"
            type="number"
            value={algorithmParams?.fibonacci_1}
            onChange={(e) => handleParamChange('fibonacci_1', parseInt(e?.target?.value))}
            description="First Fibonacci level"
          />
          <Input
            label="Fibonacci Level 2"
            type="number"
            value={algorithmParams?.fibonacci_2}
            onChange={(e) => handleParamChange('fibonacci_2', parseInt(e?.target?.value))}
            description="Second Fibonacci level"
          />
          <Input
            label="Fibonacci Level 3"
            type="number"
            value={algorithmParams?.fibonacci_3}
            onChange={(e) => handleParamChange('fibonacci_3', parseInt(e?.target?.value))}
            description="Third Fibonacci level"
          />
          <Input
            label="Retracement 1"
            type="number"
            step="0.001"
            value={algorithmParams?.retracement_1}
            onChange={(e) => handleParamChange('retracement_1', parseFloat(e?.target?.value))}
            description="First retracement level"
          />
          <Input
            label="Retracement 2"
            type="number"
            step="0.001"
            value={algorithmParams?.retracement_2}
            onChange={(e) => handleParamChange('retracement_2', parseFloat(e?.target?.value))}
            description="Second retracement level"
          />
          <Input
            label="Retracement 3"
            type="number"
            step="0.001"
            value={algorithmParams?.retracement_3}
            onChange={(e) => handleParamChange('retracement_3', parseFloat(e?.target?.value))}
            description="Third retracement level"
          />
          <Input
            label="Retracement 4"
            type="number"
            step="0.001"
            value={algorithmParams?.retracement_4}
            onChange={(e) => handleParamChange('retracement_4', parseFloat(e?.target?.value))}
            description="Fourth retracement level"
          />
          <Input
            label="Golden Ratio 1"
            type="number"
            step="0.001"
            value={algorithmParams?.golden_ratio_1}
            onChange={(e) => handleParamChange('golden_ratio_1', parseFloat(e?.target?.value))}
            description="Primary golden ratio"
          />
        </div>
      </div>
      {/* Risk Management */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Risk Management</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Risk Factor"
            type="number"
            step="0.01"
            value={algorithmParams?.risk_factor}
            onChange={(e) => handleParamChange('risk_factor', parseFloat(e?.target?.value))}
            description="Overall risk factor (0-1)"
          />
          <Input
            label="Position Size"
            type="number"
            value={algorithmParams?.position_size}
            onChange={(e) => handleParamChange('position_size', parseInt(e?.target?.value))}
            description="Default position size percentage"
          />
          <Input
            label="Stop Loss"
            type="number"
            step="0.1"
            value={algorithmParams?.stop_loss}
            onChange={(e) => handleParamChange('stop_loss', parseFloat(e?.target?.value))}
            description="Stop loss percentage"
          />
          <Input
            label="Take Profit"
            type="number"
            step="0.1"
            value={algorithmParams?.take_profit}
            onChange={(e) => handleParamChange('take_profit', parseFloat(e?.target?.value))}
            description="Take profit percentage"
          />
          <Input
            label="Trailing Stop"
            type="number"
            step="0.1"
            value={algorithmParams?.trailing_stop}
            onChange={(e) => handleParamChange('trailing_stop', parseFloat(e?.target?.value))}
            description="Trailing stop percentage"
          />
          <Input
            label="Max Positions"
            type="number"
            value={algorithmParams?.max_positions}
            onChange={(e) => handleParamChange('max_positions', parseInt(e?.target?.value))}
            description="Maximum concurrent positions"
          />
        </div>
      </div>
      {/* Preview Mode */}
      {isPreviewMode && (
        <div className="bg-muted border border-border rounded-lg p-6">
          <h4 className="text-md font-medium text-foreground mb-4 flex items-center">
            <Icon name="Eye" size={16} className="mr-2" />
            Algorithm Preview
          </h4>
          <div className="bg-background rounded-md p-4">
            <div className="text-sm text-muted-foreground mb-2">Current Configuration Summary:</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Strategy:</span>
                <span className="ml-2 text-foreground font-medium">{selectedPreset}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Risk Level:</span>
                <span className="ml-2 text-foreground font-medium">{algorithmParams?.risk_factor * 100}%</span>
              </div>
              <div>
                <span className="text-muted-foreground">Signal Strength:</span>
                <span className="ml-2 text-foreground font-medium">{algorithmParams?.signal_strength}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Max Positions:</span>
                <span className="ml-2 text-foreground font-medium">{algorithmParams?.max_positions}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Save Actions */}
      <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
        <Button variant="outline" onClick={handleResetToDefaults}>
          Reset to Defaults
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left">
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default AlgorithmSettings;